import type { CSSProperties } from 'react'
import type { PurchasingProductRow } from '@/services/purchasingTypes'
import type { FieldDefinition, FieldOption } from '@/tableControls'
import { readStoredColumnWidths, saveColumnWidths } from '@/tableControls'

export const PURCHASING_PRODUCTS_PAGE_KEY = 'purchasing-products'

export const PURCHASING_PRODUCTS_COLUMN_KEYS = [
  'name',
  'family',
  'atomic_uom',
  'vendor_item_count',
  'last_cost_per_atomic_unit',
  'brand_policy',
  'status',
  'meez_ingredient',
] as const

export type PurchasingProductsColumnKey = (typeof PURCHASING_PRODUCTS_COLUMN_KEYS)[number]

export const PURCHASING_PRODUCTS_DEFAULT_WIDTHS: Record<string, number> = {
  name: 220,
  family: 140,
  atomic_uom: 96,
  vendor_item_count: 88,
  last_cost_per_atomic_unit: 120,
  brand_policy: 112,
  status: 100,
  meez_ingredient: 180,
}

export const PURCHASING_PRODUCTS_COLUMN_LABELS: Record<PurchasingProductsColumnKey, string> = {
  name: 'Name',
  family: 'Family',
  atomic_uom: 'Atomic UOM',
  vendor_item_count: 'Vendors',
  last_cost_per_atomic_unit: 'Last $/atomic',
  brand_policy: 'Brand policy',
  status: 'Status',
  meez_ingredient: 'Meez ingredient',
}

const SORTABLE_COLUMN_KEYS = new Set<PurchasingProductsColumnKey>([
  'name',
  'family',
  'atomic_uom',
  'vendor_item_count',
  'last_cost_per_atomic_unit',
  'brand_policy',
  'status',
  'meez_ingredient',
])

export const PURCHASING_PRODUCTS_DEFAULT_SORT = [{ field: 'name', direction: 'asc' as const }]

export const PURCHASING_PRODUCTS_GROUP_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'family', label: 'Family' },
  { value: 'atomic_uom', label: 'Atomic UOM' },
  { value: 'brand_policy', label: 'Brand policy' },
  { value: 'status', label: 'Status' },
]

export const PRODUCT_STATUS_FILTER_OPTIONS: FieldOption[] = [
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
  { value: 'merged', label: 'Merged' },
]

export const BRAND_POLICY_FILTER_OPTIONS: FieldOption[] = [
  { value: 'agnostic', label: 'Agnostic' },
  { value: 'preferred', label: 'Preferred' },
  { value: 'required', label: 'Required' },
]

export const PURCHASING_PRODUCTS_FIELD_DEFINITIONS: FieldDefinition[] =
  PURCHASING_PRODUCTS_COLUMN_KEYS.map((key) => {
    const type =
      key === 'vendor_item_count' || key === 'last_cost_per_atomic_unit'
        ? 'number'
        : key === 'brand_policy' || key === 'status'
          ? 'select'
          : 'text'
    const accessor = (row: unknown) => {
      const product = row as PurchasingProductRow
      if (key === 'meez_ingredient') {
        return product.meez_ingredient_name ?? product.meez_ingredient_id ?? ''
      }
      return product[key as keyof PurchasingProductRow]
    }
    return {
      key,
      label: PURCHASING_PRODUCTS_COLUMN_LABELS[key],
      type,
      defaultVisible: true,
      sortable: SORTABLE_COLUMN_KEYS.has(key),
      accessor,
      ...(key === 'brand_policy'
        ? { options: BRAND_POLICY_FILTER_OPTIONS }
        : key === 'status'
          ? { options: PRODUCT_STATUS_FILTER_OPTIONS }
          : {}),
    }
  })

export function fixedColumnStyle(width: number): CSSProperties {
  return {
    width,
    minWidth: width,
    maxWidth: width,
    boxSizing: 'border-box',
  }
}

export function isPurchasingProductsColumnSortable(key: string): boolean {
  return SORTABLE_COLUMN_KEYS.has(key as PurchasingProductsColumnKey)
}

export function buildPurchasingProductsFilterFields(
  familyOptions: FieldOption[],
  atomicUomOptions: FieldOption[],
  cogsAccountOptions: FieldOption[],
): FieldDefinition[] {
  return [
    {
      key: 'family',
      label: 'Family',
      type: 'multiSelect',
      filterable: true,
      options: familyOptions,
      accessor: (row) => (row as PurchasingProductRow).family ?? '',
    },
    {
      key: 'atomic_uom',
      label: 'Atomic UOM',
      type: 'multiSelect',
      filterable: true,
      options: atomicUomOptions,
      accessor: (row) => (row as PurchasingProductRow).atomic_uom,
    },
    {
      key: 'brand_policy',
      label: 'Brand policy',
      type: 'multiSelect',
      filterable: true,
      options: BRAND_POLICY_FILTER_OPTIONS,
      accessor: (row) => (row as PurchasingProductRow).brand_policy,
    },
    {
      key: 'status',
      label: 'Status',
      type: 'multiSelect',
      filterable: true,
      options: PRODUCT_STATUS_FILTER_OPTIONS,
      accessor: (row) => (row as PurchasingProductRow).status,
    },
    {
      key: 'cogs_account_id',
      label: 'COGS account',
      type: 'multiSelect',
      filterable: true,
      options: cogsAccountOptions,
      accessor: (row) => (row as PurchasingProductRow).cogs_account_id,
    },
  ]
}

export function buildPurchasingProductsExportMatrix(rows: PurchasingProductRow[]): {
  headers: string[]
  rows: string[][]
} {
  const headers = PURCHASING_PRODUCTS_COLUMN_KEYS.map(
    (key) => PURCHASING_PRODUCTS_COLUMN_LABELS[key],
  )
  const exportRows = rows.map((row) =>
    PURCHASING_PRODUCTS_COLUMN_KEYS.map((key) => {
      if (key === 'meez_ingredient') {
        return row.meez_ingredient_name ?? row.meez_ingredient_id ?? ''
      }
      const value = row[key as keyof PurchasingProductRow]
      return value == null ? '' : String(value)
    }),
  )
  return { headers, rows: exportRows }
}

export function migrateLegacyPurchasingProductsColumnWidths(): void {
  const existing = readStoredColumnWidths(PURCHASING_PRODUCTS_PAGE_KEY)
  if (Object.keys(existing).length > 0) return
  const legacyKey = 'bookkeeper:purchasing-products-widths'
  try {
    const raw = localStorage.getItem(legacyKey)
    if (!raw) return
    const parsed = JSON.parse(raw) as Record<string, unknown>
    const widths: Record<string, number> = {}
    for (const key of PURCHASING_PRODUCTS_COLUMN_KEYS) {
      const value = parsed[key]
      if (typeof value === 'number' && Number.isFinite(value)) {
        widths[key] = value
      }
    }
    if (Object.keys(widths).length > 0) {
      saveColumnWidths(PURCHASING_PRODUCTS_PAGE_KEY, widths)
    }
  } catch {
    /* ignore */
  }
}
